import { useMemo, useState } from "react";
import {
  Building2,
  Check,
  ChevronRight,
  Info,
  RotateCcw,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  Layers,
  Lightbulb,
  Linkedin,
  MapPin,
  Network,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Star,
  Trash2,
  Undo2,
  Users,
  X,
} from "lucide-react";
import {
  TIERS,
  TIER_ORDER,
  TERRITORIES,
  TERRITORY_ORDER,
  STATE_NAME,
} from "../data/broker";
import { computeOutreachSuggestions } from "../utils/outreach";
import { API } from "../auth/api";
import TpaNetworksPanel from "./TpaNetworksPanel";

function TierPill({ tier, active, count, onClick }) {
  const meta = tier === "all" ? { short: "All", color: "#22D3EE", label: "" } : TIERS[tier];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`tier-btn-${tier}`}
      className={`flex-1 min-w-0 px-2 py-2 border text-left transition-colors ${
        active
          ? "bg-[#141418] border-transparent"
          : "border-white/10 hover:border-white/25 bg-[#0d0d10]"
      }`}
      style={active ? { borderColor: meta.color } : undefined}
    >
      <div className="flex items-baseline gap-1.5">
        <span
          className="font-mono text-[12px] font-semibold tabular-nums"
          style={{ color: meta.color }}
        >
          {meta.short}
        </span>
        <span className="font-mono text-[9px] text-zinc-500 tabular-nums">{count}</span>
      </div>
      {tier !== "all" && (
        <div className="font-mono text-[8px] uppercase tracking-[0.14em] text-zinc-500 mt-1 truncate">
          {TIERS[tier].label}
        </div>
      )}
    </button>
  );
}

function TerritoryPill({ terr, active, count, onClick }) {
  const meta = terr === "all" ? { short: "ALL", label: "All Regions" } : TERRITORIES[terr];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`terr-btn-${terr}`}
      className={`px-2.5 py-1.5 border font-mono text-[10px] tracking-[0.16em] uppercase transition-colors flex items-center gap-1.5 ${
        active
          ? "border-cyan-400/70 text-cyan-200 bg-cyan-400/10"
          : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-zinc-200"
      }`}
    >
      {meta.short}
      <span className="text-zinc-500 tabular-nums normal-case">{count}</span>
    </button>
  );
}

function ContactRow({ c, onSelect, onEdit, connectionCount = 0 }) {
  const tier = TIERS[c.tier] || TIERS.t5;
  return (
    <div
      className="group flex items-start gap-2 px-3 py-2 border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
      onClick={() => onSelect && onSelect(c.id)}
      data-testid={`contact-row-${c.id}`}
    >
      <span
        className="mt-1 w-2 h-2 rounded-full shrink-0"
        style={{ background: tier.color, boxShadow: `0 0 8px ${tier.glow}` }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-zinc-100 text-[12px] truncate">
            {c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}
          </span>
          <span className="font-mono text-[9px] tracking-[0.16em] uppercase" style={{ color: tier.color }}>
            {tier.short}
          </span>
          {connectionCount > 0 && (
            <span
              className="ml-auto flex items-center gap-1 font-mono text-[9px] tracking-[0.16em] uppercase text-fuchsia-300 border border-fuchsia-400/30 px-1.5 py-0.5"
              aria-label="Connections" title="Connections"
            >
              <Network className="w-2.5 h-2.5" />
              {connectionCount}
            </span>
          )}
        </div>
        <div className="text-[11px] text-zinc-400 truncate">
          {c.company_name}
          {c.state && <span className="text-zinc-600"> · {c.state}</span>}
        </div>
      </div>
      <div className="row-actions flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {c.hubspot_contact_link && (
          <a
            href={c.hubspot_contact_link}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="w-6 h-6 flex items-center justify-center border border-white/10 hover:border-orange-400/50 hover:text-orange-300 text-zinc-400"
            aria-label="HubSpot" title="HubSpot"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {c.linkedin_url && (
          <a
            href={c.linkedin_url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="w-6 h-6 flex items-center justify-center border border-white/10 hover:border-sky-400/50 hover:text-sky-300 text-zinc-400"
            aria-label="LinkedIn" title="LinkedIn"
          >
            <Linkedin className="w-3 h-3" />
          </a>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit && onEdit(c.id);
          }}
          className="w-6 h-6 flex items-center justify-center border border-white/10 hover:border-cyan-400/50 hover:text-cyan-300 text-zinc-400"
          aria-label="Edit" title="Edit"
          data-testid={`edit-contact-${c.id}`}
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function FirmRow({ firm, tierMix, contactCount, focused, onFocus, onEdit, onDelete }) {
  const tint = firm.is_no_contact ? TIERS.t5.color : "#22D3EE";
  return (
    <div
      className={`group flex items-start gap-2 px-3 py-2 border-b border-white/5 cursor-pointer ${
        focused ? "bg-cyan-400/10" : "hover:bg-white/[0.03]"
      }`}
      onClick={() => onFocus && onFocus(firm.id)}
      data-testid={`firm-row-${firm.id}`}
    >
      <span className="mt-1 w-2 h-2 rounded-full shrink-0" style={{ background: tint }} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-zinc-100 text-[12px] truncate">
            {firm.name}
          </span>
          {firm.is_no_contact && (
            <span
              className="font-mono text-[9px] tracking-[0.16em] uppercase px-1.5 py-0.5 border border-zinc-600/60 text-zinc-400"
            >
              T5
            </span>
          )}
        </div>
        <div className="text-[10px] text-zinc-500 mt-0.5 flex flex-wrap gap-1.5">
          {tierMix.map((t) => (
            <span
              key={t.tier}
              className="font-mono uppercase tracking-[0.14em]"
              style={{ color: TIERS[t.tier].color }}
            >
              {TIERS[t.tier].short}·{t.count}
            </span>
          ))}
          {firm.state && (
            <span className="font-mono uppercase tracking-[0.14em] text-zinc-500">
              {firm.state}
            </span>
          )}
          <span className="font-mono uppercase tracking-[0.14em] text-zinc-600 ml-auto">
            {contactCount} contacts
          </span>
        </div>
      </div>
      <div className="row-actions flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {firm.company_hubspot_link && (
          <a
            href={firm.company_hubspot_link}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="w-6 h-6 flex items-center justify-center border border-white/10 hover:border-orange-400/50 hover:text-orange-300 text-zinc-400"
            aria-label="HubSpot" title="HubSpot"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit && onEdit(firm.id);
          }}
          className="w-6 h-6 flex items-center justify-center border border-white/10 hover:border-cyan-400/50 hover:text-cyan-300 text-zinc-400"
          aria-label="Edit" title="Edit"
          data-testid={`edit-firm-${firm.id}`}
        >
          <Pencil className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete && onDelete(firm.id);
          }}
          className="w-6 h-6 flex items-center justify-center border border-white/10 hover:border-rose-400/50 hover:text-rose-300 text-zinc-400"
          aria-label="Delete" title="Delete"
          data-testid={`delete-firm-${firm.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

export default function SalesSidebar({
  contacts,
  salesTeam,
  firms,
  connections,
  scope,
  setScope,
  abmContactTotal,
  allContactTotal,
  activeTab,
  setActiveTab,
  // Networks tab
  tpaData,
  scopedTpaCoverage,
  selectedTpa,
  setSelectedTpa,
  focusedNetworkState,
  setFocusedNetworkState,
  // TPA Directory
  tpaContacts,
  expandedTpaName,
  setExpandedTpaName,
  showTpaContactsOnMap,
  setShowTpaContactsOnMap,
  onAddTpaContact,
  onEditTpaContact,
  onDeleteTpaContact,
  onImportTpaContactsCsv,
  activeTier,
  setActiveTier,
  activeTerritory,
  setActiveTerritory,
  visibleTiers,
  showStates,
  setShowStates,
  choropleth,
  setChoropleth,
  hoveredState,
  query,
  setQuery,
  focusedFirmId,
  setFocusedFirmId,
  onEditFirm,
  onDeleteFirm,
  onEditContact,
  onSelect,
  contactConnectionCount,
  selectedContact,
  selectedPeers,
  showConnectionsPanel,
  onShowConnectionsPanel,
  onStartConnect,
  onRemoveConnection,
  onOpenSuggestion,
  onCompleteSignal,
  onUncompleteSignal,
  isolatedIds,
  onClearIsolation,
}) {
  const tab = activeTab || "contacts";
  const setTab = setActiveTab || (() => {});
  const [signalOwnerFilter, setSignalOwnerFilter] = useState("all");
  const [contactLimit, setContactLimit] = useState(400);
  const [showCompletedSignals, setShowCompletedSignals] = useState(false);
  const [busySignalId, setBusySignalId] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);

  const downloadBackup = async () => {
    const password = window.prompt(
      "Re-enter your own account password to download the full backup:",
      "",
    );
    if (!password) return;
    setBackupBusy(true);
    try {
      const res = await API.post(
        "/backup",
        { password },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `virtue-backup-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e?.response?.data?.detail || e.message || "please try again.";
      // Axios blob responseType stringifies error blobs; try to parse.
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          detail = j.detail || txt;
        } catch {
          /* ignore parse fail */
        }
      }
      window.alert("Backup failed. " + detail);
    } finally {
      setBackupBusy(false);
    }
  };
  // Auto-open Connections panel when a peer list becomes relevant.
  const connPanelOpen = showConnectionsPanel && Boolean(selectedContact);

  const allSuggestions = useMemo(
    () => computeOutreachSuggestions(contacts, connections || []),
    [contacts, connections],
  );

  // Open (not-yet-completed) plays drive the tab badge — completed plays are
  // hidden by default so the queue always reflects work left to do.
  const openSuggestions = useMemo(
    () => allSuggestions.filter((s) => !s.completed_at),
    [allSuggestions],
  );

  // Unique contact_owner values across the current dataset. Sales-team names
  // are pinned to the top so a rep can find themselves quickly.
  const ownerOptions = useMemo(() => {
    const all = new Set();
    for (const c of contacts) {
      const o = (c.contact_owner || "").trim();
      if (o) all.add(o);
    }
    const salesFirstNames = new Set(
      (salesTeam || []).map((s) => (s.first_name || "").trim()).filter(Boolean),
    );
    const salesOwners = [];
    const rest = [];
    for (const name of Array.from(all).sort()) {
      const first = name.split(/\s+/)[0];
      if (salesFirstNames.has(first)) salesOwners.push(name);
      else rest.push(name);
    }
    return { salesOwners, rest };
  }, [contacts, salesTeam]);

  const suggestions = useMemo(() => {
    const source = showCompletedSignals ? allSuggestions : openSuggestions;
    if (signalOwnerFilter === "all") return source;
    return source.filter(
      (s) => (s.warm.contact_owner || "").trim() === signalOwnerFilter,
    );
  }, [allSuggestions, openSuggestions, signalOwnerFilter, showCompletedSignals]);

  // ---- Counts ------------------------------------------------------------
  const tierCounts = useMemo(() => {
    const c = { total: 0, byTier: {} };
    TIER_ORDER.forEach((t) => (c.byTier[t] = 0));
    for (const x of contacts) {
      if (activeTerritory !== "all" && x.territory !== activeTerritory) continue;
      c.total += 1;
      if (x.tier && c.byTier[x.tier] !== undefined) c.byTier[x.tier] += 1;
    }
    return c;
  }, [contacts, activeTerritory]);

  const territoryCounts = useMemo(() => {
    const c = { total: 0, byTerr: {} };
    TERRITORY_ORDER.forEach((t) => (c.byTerr[t] = 0));
    for (const x of contacts) {
      if (!visibleTiers[x.tier]) continue;
      c.total += 1;
      if (x.territory && c.byTerr[x.territory] !== undefined) c.byTerr[x.territory] += 1;
    }
    return c;
  }, [contacts, visibleTiers]);

  // Contact list filtered by all current filters + search
  const filteredContacts = useMemo(() => {
    const q = query.trim().toLowerCase();
    const wordMatch = (s) => (s || "").toLowerCase().includes(q);
    // Isolation mode short-circuits every other filter.
    if (isolatedIds && isolatedIds.size > 0) {
      return contacts.filter((c) => isolatedIds.has(c.id));
    }
    return contacts.filter((c) => {
      if (!visibleTiers[c.tier]) return false;
      if (activeTerritory !== "all" && c.territory !== activeTerritory) return false;
      if (focusedFirmId && c.firm_id !== focusedFirmId) return false;
      if (!q) return true;
      return (
        wordMatch(c.name) ||
        wordMatch(c.company_name) ||
        wordMatch(c.email) ||
        wordMatch(c.state) ||
        wordMatch(c.contact_owner)
      );
    });
  }, [contacts, visibleTiers, activeTerritory, focusedFirmId, query, isolatedIds]);

  // Firms: derive tier mix from contacts, include Tier-5 no-contact firms
  const firmSummary = useMemo(() => {
    const contactsByFirm = new Map();
    for (const c of contacts) {
      if (!c.firm_id) continue;
      if (!contactsByFirm.has(c.firm_id)) contactsByFirm.set(c.firm_id, []);
      contactsByFirm.get(c.firm_id).push(c);
    }
    return firms.map((f) => {
      const list = contactsByFirm.get(f.id) || [];
      const tierMap = {};
      for (const c of list) tierMap[c.tier] = (tierMap[c.tier] || 0) + 1;
      const tierMix = Object.entries(tierMap)
        .map(([tier, count]) => ({ tier, count }))
        .sort((a, b) => (TIERS[a.tier]?.ordinal || 9) - (TIERS[b.tier]?.ordinal || 9));
      return { firm: f, tierMix, contactCount: list.length };
    });
  }, [contacts, firms]);

  const filteredFirms = useMemo(() => {
    const q = query.trim().toLowerCase();
    return firmSummary.filter(({ firm, tierMix }) => {
      // Filter to those matching active tier + territory
      if (activeTier !== "all") {
        // For no-contact firms, only show in T5 view
        if (firm.is_no_contact) {
          if (activeTier !== "t5") return false;
        } else if (!tierMix.some((t) => t.tier === activeTier)) {
          return false;
        }
      }
      // Territory filter: only relevant for firms with contacts
      if (activeTerritory !== "all" && !firm.is_no_contact) {
        const hasTerr = contacts.some(
          (c) => c.firm_id === firm.id && c.territory === activeTerritory,
        );
        if (!hasTerr) return false;
      }
      if (!q) return true;
      return (firm.name || "").toLowerCase().includes(q);
    });
  }, [firmSummary, activeTier, activeTerritory, contacts, query]);

  return (
    <aside
      className="flex flex-col h-full bg-[#0a0a0c] border-r border-white/10 w-full md:w-[380px] shrink-0 overflow-hidden"
      data-testid="sales-sidebar"
    >
      {/* Brand */}
      <div
        className="px-6 pt-5 pb-4 border-b border-white/10 flex items-center gap-2"
        data-testid="sidebar-brand"
      >
        <img
          src="/virtue-logo.png"
          alt=""
          className="h-5 w-auto shrink-0 block"
          data-testid="sidebar-logo"
        />
        <span className="font-mono text-[10px] tracking-[0.28em] text-zinc-400 uppercase">
          Virtue Health
        </span>
        <span className="ml-auto font-mono text-[10px] text-zinc-600">v1.0</span>
      </div>

      {/* Scope selector — ABM vs All Data */}
      <div
        className="px-4 pt-3 pb-3 border-b border-white/10 bg-[#0b0b0e]"
        data-testid="scope-selector"
      >
        <div className="font-mono text-[9px] tracking-[0.22em] text-zinc-500 uppercase mb-2">
          // Data Scope
        </div>
        <div className="grid grid-cols-2 gap-1">
          <button
            type="button"
            onClick={() => setScope && setScope("abm")}
            data-testid="scope-btn-abm"
            className={`px-2.5 py-2 border font-mono text-[10px] tracking-[0.2em] uppercase text-left transition-colors ${
              scope === "abm"
                ? "bg-[#141418] text-amber-200 border-amber-400/70"
                : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-zinc-200"
            }`}
          >
            <div className="flex items-baseline gap-1.5">
              <Star className="w-3 h-3" />
              <span className="font-semibold">ABM</span>
              <span className="ml-auto text-[9px] text-zinc-500 tabular-nums normal-case tracking-normal">
                {abmContactTotal ?? 0}
              </span>
            </div>
            <div className="mt-1 font-mono text-[8px] tracking-[0.14em] uppercase text-zinc-500 truncate">
              Target Account List
            </div>
          </button>
          <button
            type="button"
            onClick={() => setScope && setScope("all")}
            data-testid="scope-btn-all"
            className={`px-2.5 py-2 border font-mono text-[10px] tracking-[0.2em] uppercase text-left transition-colors ${
              scope === "all"
                ? "bg-[#141418] text-cyan-200 border-cyan-400/70"
                : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-zinc-200"
            }`}
          >
            <div className="flex items-baseline gap-1.5">
              <Layers className="w-3 h-3" />
              <span className="font-semibold">All Data</span>
              <span className="ml-auto text-[9px] text-zinc-500 tabular-nums normal-case tracking-normal">
                {allContactTotal ?? 0}
              </span>
            </div>
            <div className="mt-1 font-mono text-[8px] tracking-[0.14em] uppercase text-zinc-500 truncate">
              Full HubSpot Universe
            </div>
          </button>
        </div>
      </div>

      {/* Tier selector */}
      <div className="px-4 pt-3 pb-3 border-b border-white/10 bg-[#0b0b0e]" data-testid="tier-selector">
        <div className="font-mono text-[9px] tracking-[0.22em] text-zinc-500 uppercase mb-2">
          // Tier View
        </div>
        <div className="flex gap-1">
          <TierPill
            tier="all"
            active={activeTier === "all"}
            count={tierCounts.total}
            onClick={() => setActiveTier("all")}
          />
          {TIER_ORDER.map((tk) => (
            <TierPill
              key={tk}
              tier={tk}
              active={activeTier === tk}
              count={tierCounts.byTier[tk]}
              onClick={() => setActiveTier(tk)}
            />
          ))}
        </div>
        {activeTier !== "all" && (
          <div className="mt-2 font-mono text-[10px] text-zinc-400 leading-relaxed">
            <span className="text-zinc-500">Focus: </span>
            <span style={{ color: TIERS[activeTier].color }}>
              {TIERS[activeTier].short} · {TIERS[activeTier].label}
            </span>
            <span className="text-zinc-500"> — {TIERS[activeTier].desc}</span>
          </div>
        )}
      </div>

      {/* Territory selector */}
      <div className="px-4 pt-3 pb-3 border-b border-white/10" data-testid="territory-selector">
        <div className="font-mono text-[9px] tracking-[0.22em] text-zinc-500 uppercase mb-2">
          // Territory
        </div>
        <div className="flex flex-wrap gap-1.5">
          <TerritoryPill
            terr="all"
            active={activeTerritory === "all"}
            count={territoryCounts.total}
            onClick={() => setActiveTerritory("all")}
          />
          {TERRITORY_ORDER.map((tk) => (
            <TerritoryPill
              key={tk}
              terr={tk}
              active={activeTerritory === tk}
              count={territoryCounts.byTerr[tk]}
              onClick={() => setActiveTerritory(tk)}
            />
          ))}
        </div>
      </div>

      {/* Tabs — split onto two rows so labels aren't cramped */}
      {(() => {
        const TABS = [
          { id: "contacts", label: "Contacts", icon: Users },
          { id: "firms", label: "Firms", icon: Building2 },
          { id: "signals", label: "Signals", icon: Lightbulb, badge: openSuggestions.length },
          { id: "tools", label: "Tools", icon: Sparkles },
          { id: "sales-team", label: "Sales Team", icon: Star, badge: salesTeam?.length || 0 },
          { id: "networks", label: "Networks", icon: Layers },
        ];
        const renderTab = ({ id, label, icon: Icon, badge }) => (
          <button
            key={id}
            data-testid={`tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${
              tab === id
                ? "text-zinc-50 bg-[#121215] border-b-2 border-cyan-400 -mb-[1px]"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <Icon className="w-3 h-3" />
            {label}
            {badge > 0 && (
              <span
                className={`ml-0.5 font-mono text-[9px] px-1 leading-none py-0.5 border ${
                  tab === id ? "border-cyan-400/60 text-cyan-200" : "border-white/15 text-zinc-400"
                }`}
              >
                {badge}
              </span>
            )}
          </button>
        );
        return (
          <>
            <div className="flex border-b border-white/10">{TABS.slice(0, 4).map(renderTab)}</div>
            <div className="flex border-b border-white/10">{TABS.slice(4).map(renderTab)}</div>
          </>
        );
      })()}

      {/* Isolation banner — pins map to a single warm/cold pair */}
      {isolatedIds && isolatedIds.size > 0 && (
        <div
          className="px-4 py-2 border-b border-fuchsia-400/30 bg-fuchsia-400/5 flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-fuchsia-300"
          data-testid="isolation-banner"
        >
          <Lightbulb className="w-3 h-3" />
          <span className="flex-1 truncate">
            Isolated Signal · {isolatedIds.size} contacts on map
          </span>
          <button
            type="button"
            onClick={() => onClearIsolation && onClearIsolation()}
            data-testid="clear-isolation"
            className="w-5 h-5 flex items-center justify-center border border-white/10 hover:border-rose-400/60 hover:text-rose-300 text-zinc-400"
            title="Show all contacts again"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Firm focus indicator */}
      {focusedFirmId && (
        <div className="px-4 py-2 border-b border-cyan-400/30 bg-cyan-400/5 flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-cyan-300">
          <MapPin className="w-3 h-3" />
          <span className="flex-1 truncate">
            Focus · {firms.find((f) => f.id === focusedFirmId)?.name || focusedFirmId}
          </span>
          <button
            type="button"
            onClick={() => setFocusedFirmId(null)}
            data-testid="clear-firm-focus"
            className="w-5 h-5 flex items-center justify-center border border-white/10 hover:border-rose-400/60 hover:text-rose-300 text-zinc-400"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Connections panel — shown when a contact is selected and panel toggled */}
      {connPanelOpen && (
        <div
          className="border-b border-fuchsia-400/30 bg-fuchsia-400/5"
          data-testid="connections-panel"
        >
          <div className="px-4 py-2 flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-fuchsia-300">
            <Network className="w-3 h-3" />
            <span className="flex-1 truncate normal-case tracking-normal">
              {selectedContact.name ||
                `${selectedContact.first_name || ""} ${selectedContact.last_name || ""}`.trim() ||
                "Contact"}
              <span className="text-zinc-500"> — {selectedPeers?.length || 0} link{selectedPeers?.length === 1 ? "" : "s"}</span>
            </span>
            <button
              type="button"
              onClick={() => onStartConnect && onStartConnect(selectedContact.id)}
              data-testid="sidebar-add-connection"
              className="flex items-center gap-1 border border-fuchsia-400/40 hover:border-fuchsia-300 hover:bg-fuchsia-400/10 px-2 py-1 text-fuchsia-200"
              title="Add connection"
            >
              <Plus className="w-3 h-3" />
              <span className="normal-case tracking-normal">Connect</span>
            </button>
            <button
              type="button"
              onClick={() => onShowConnectionsPanel && onShowConnectionsPanel(false)}
              data-testid="close-connections-panel"
              className="w-5 h-5 flex items-center justify-center border border-white/10 hover:border-rose-400/60 hover:text-rose-300 text-zinc-400"
              title="Close"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="max-h-[220px] overflow-y-auto">
            {(!selectedPeers || selectedPeers.length === 0) && (
              <div className="px-4 py-4 font-mono text-[10px] text-zinc-500 tracking-[0.16em] uppercase">
                No connections yet — press <span className="text-fuchsia-300">+ Connect</span> to link a peer.
              </div>
            )}
            {selectedPeers?.map(({ connection, peer }) => {
              const tier = TIERS[peer.tier] || TIERS.t5;
              return (
                <div
                  key={connection.id}
                  className="group flex items-start gap-2 px-4 py-2 hover:bg-white/[0.03] border-b border-white/5 cursor-pointer"
                  onClick={() => onSelect && onSelect(peer.id)}
                  data-testid={`peer-row-${peer.id}`}
                >
                  <span
                    className="mt-1 w-2 h-2 rounded-full shrink-0"
                    style={{ background: tier.color, boxShadow: `0 0 8px ${tier.glow}` }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-100 text-[12px] truncate">
                        {peer.name ||
                          `${peer.first_name || ""} ${peer.last_name || ""}`.trim() ||
                          "—"}
                      </span>
                      <span
                        className="font-mono text-[9px] tracking-[0.16em] uppercase"
                        style={{ color: tier.color }}
                      >
                        {tier.short}
                      </span>
                    </div>
                    <div className="text-[10px] text-zinc-500 truncate">
                      {peer.company_name}
                      {peer.state && <span className="text-zinc-600"> · {peer.state}</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveConnection && onRemoveConnection(connection.id);
                    }}
                    className="row-actions w-6 h-6 flex items-center justify-center border border-white/10 hover:border-rose-400/50 hover:text-rose-300 text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove connection"
                    data-testid={`remove-connection-${connection.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Show a compact "View Connections" chip whenever a contact is selected but panel closed */}
      {!connPanelOpen && selectedContact && (
        <div
          className="px-4 py-2 border-b border-white/10 bg-[#0b0b0e] flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-400"
          data-testid="selected-contact-chip"
        >
          <span className="text-fuchsia-300">Selected:</span>
          <span className="flex-1 truncate normal-case tracking-normal text-zinc-200">
            {selectedContact.name ||
              `${selectedContact.first_name || ""} ${selectedContact.last_name || ""}`.trim()}
          </span>
          <button
            type="button"
            onClick={() => onShowConnectionsPanel && onShowConnectionsPanel(true)}
            data-testid="open-connections-panel"
            className="flex items-center gap-1 border border-fuchsia-400/40 hover:border-fuchsia-300 hover:bg-fuchsia-400/10 px-2 py-1 text-fuchsia-200"
          >
            <Network className="w-3 h-3" />
            <span>Connections</span>
          </button>
          <button
            type="button"
            onClick={() => onSelect && onSelect(null)}
            data-testid="clear-selected-contact"
            className="w-5 h-5 flex items-center justify-center border border-white/10 hover:border-rose-400/60 hover:text-rose-300 text-zinc-400"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Search */}
      {(tab === "contacts" || tab === "firms") && (
        <div className="px-4 pt-3 pb-2 border-b border-white/10">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tab === "contacts" ? "search name / company / owner…" : "search firm…"}
              data-testid="sidebar-search"
              className="w-full pl-8 pr-2 py-2 bg-[#0d0d10] border border-white/10 focus:border-cyan-400/50 outline-none font-mono text-[11px] text-zinc-200 placeholder:text-zinc-600"
            />
          </div>
        </div>
      )}

      {/* Panel body */}
      <div className="flex-1 min-h-0 overflow-auto" data-testid="sidebar-scroll">
        {tab === "contacts" && (
          <div data-testid="contacts-panel">
            <div className="px-4 py-2 flex items-center justify-between border-b border-white/5 sticky top-0 bg-[#0a0a0c]">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500">
                Contacts
              </span>
              <span className="font-mono text-[10px] tabular-nums text-zinc-500">
                {filteredContacts.length}
              </span>
            </div>
            {filteredContacts.slice(0, contactLimit).map((c) => (
              <ContactRow
                key={c.id}
                c={c}
                onSelect={onSelect}
                onEdit={onEditContact}
                connectionCount={contactConnectionCount?.get(c.id) || 0}
              />
            ))}
            {filteredContacts.length > contactLimit && (
              <button
                type="button"
                data-testid="contacts-load-more"
                onClick={() => setContactLimit((n) => n + 400)}
                className="w-full px-4 py-3 font-mono text-[10px] text-zinc-400 hover:text-emerald-300 tracking-[0.16em] uppercase text-left transition-colors"
              >
                Showing {contactLimit} of {filteredContacts.length} — load more
              </button>
            )}
            {filteredContacts.length === 0 && (
              <div className="px-4 py-6 text-center font-mono text-[10px] text-zinc-500 tracking-[0.18em] uppercase">
                No contacts match these filters
              </div>
            )}
          </div>
        )}

        {tab === "firms" && (
          <div data-testid="firms-panel">
            <div className="px-4 py-2 flex items-center justify-between border-b border-white/5 sticky top-0 bg-[#0a0a0c]">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500">
                Firms
              </span>
              <span className="font-mono text-[10px] tabular-nums text-zinc-500">
                {filteredFirms.length}
              </span>
            </div>
            {filteredFirms.map(({ firm, tierMix, contactCount }) => (
              <FirmRow
                key={firm.id}
                firm={firm}
                tierMix={tierMix}
                contactCount={contactCount}
                focused={focusedFirmId === firm.id}
                onFocus={(id) => setFocusedFirmId(focusedFirmId === id ? null : id)}
                onEdit={onEditFirm}
                onDelete={onDeleteFirm}
              />
            ))}
          </div>
        )}

        {tab === "sales-team" && (
          <div data-testid="sales-team-panel">
            <div className="px-4 py-2 flex items-center justify-between border-b border-white/5 sticky top-0 bg-[#0a0a0c]">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#B85450]">
                Virtue Health · Sales Team
              </span>
              <span className="font-mono text-[10px] tabular-nums text-zinc-500">
                {salesTeam?.length || 0}
              </span>
            </div>
            <div className="px-4 py-2 border-b border-white/5 font-mono text-[9px] tracking-[0.16em] uppercase text-zinc-500 leading-relaxed">
              Sales-team nodes render on the map only while this tab is open, or while a
              teammate is selected. Switch tabs to declutter.
            </div>
            {(salesTeam || []).map((c) => {
              const connectionCount = contactConnectionCount?.get(c.id) || 0;
              return (
                <div
                  key={c.id}
                  className="group flex items-start gap-2 px-3 py-2 border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                  onClick={() => onSelect && onSelect(c.id)}
                  data-testid={`sales-row-${c.id}`}
                >
                  <span
                    className="mt-1 w-2 h-2 rounded-full shrink-0"
                    style={{ background: TIERS.sales.color, boxShadow: `0 0 8px ${TIERS.sales.glow}` }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-zinc-100 text-[12px] truncate">
                        {c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}
                      </span>
                      <span className="font-mono text-[9px] tracking-[0.16em] uppercase text-[#B85450]">
                        VH
                      </span>
                      {connectionCount > 0 && (
                        <span className="ml-auto flex items-center gap-1 font-mono text-[9px] tracking-[0.16em] uppercase text-fuchsia-300 border border-fuchsia-400/30 px-1.5 py-0.5">
                          <Network className="w-2.5 h-2.5" />
                          {connectionCount}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-400 truncate">
                      Virtue Health · Sales Team
                    </div>
                  </div>
                  <div className="row-actions flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditContact && onEditContact(c.id);
                      }}
                      className="w-6 h-6 flex items-center justify-center border border-white/10 hover:border-cyan-400/50 hover:text-cyan-300 text-zinc-400"
                      aria-label="Edit" title="Edit"
                      data-testid={`edit-sales-${c.id}`}
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
            {(!salesTeam || salesTeam.length === 0) && (
              <div className="px-4 py-6 text-center font-mono text-[10px] text-zinc-500 tracking-[0.18em] uppercase">
                No sales team members yet
              </div>
            )}
          </div>
        )}

        {tab === "signals" && (
          <div data-testid="signals-panel">
            <div className="px-4 py-2 flex items-center justify-between border-b border-white/5 sticky top-0 bg-[#0a0a0c] gap-2">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 shrink-0">
                Outreach
              </span>
              <select
                value={signalOwnerFilter}
                onChange={(e) => setSignalOwnerFilter(e.target.value)}
                data-testid="signal-owner-filter"
                className="flex-1 min-w-0 bg-[#0d0d10] border border-white/10 focus:border-cyan-400/50 outline-none px-2 py-1 font-mono text-[11px] text-zinc-100 truncate"
              >
                <option value="all">All Owners</option>
                {ownerOptions.salesOwners.length > 0 && (
                  <optgroup label="Sales Team">
                    {ownerOptions.salesOwners.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </optgroup>
                )}
                {ownerOptions.rest.length > 0 && (
                  <optgroup label="Other Owners">
                    {ownerOptions.rest.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              <button
                type="button"
                onClick={() => setShowCompletedSignals((v) => !v)}
                data-testid="toggle-show-completed-signals"
                className={`shrink-0 flex items-center gap-1 border px-2 py-1 font-mono text-[10px] tracking-[0.14em] uppercase transition-colors ${
                  showCompletedSignals
                    ? "border-cyan-400/60 text-cyan-200 bg-cyan-400/10"
                    : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-zinc-200"
                }`}
                title={showCompletedSignals ? "Hide completed" : "Show completed"}
              >
                {showCompletedSignals ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                Done
              </button>
              <span className="font-mono text-[10px] tabular-nums text-zinc-500 shrink-0">
                {suggestions.length}
              </span>
            </div>
            {suggestions.length === 0 && (
              <div className="px-4 py-6 text-center font-mono text-[10px] text-zinc-500 tracking-[0.18em] uppercase leading-relaxed">
                {signalOwnerFilter === "all"
                  ? showCompletedSignals
                    ? "No plays yet."
                    : "All plays completed. 🎯"
                  : `No open plays for ${signalOwnerFilter}.`}
                <br />
                <span className="normal-case tracking-normal text-zinc-600 text-[11px]">
                  Link a warm contact (T1/T2) to a cold one (T3–T5) and it'll show up here.
                </span>
              </div>
            )}
            {suggestions.map((s) => {
              const wTier = TIERS[s.warm.tier] || TIERS.t5;
              const cTier = TIERS[s.cold.tier] || TIERS.t5;
              const targetTier = TIERS[s.target_tier] || TIERS.t3;
              const done = Boolean(s.completed_at);
              return (
                <div
                  key={s.id}
                  className={`px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] ${
                    done ? "opacity-55" : ""
                  }`}
                  data-testid={`suggestion-${s.id}`}
                >
                  {/* Priority header */}
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="font-mono text-[9px] tracking-[0.22em] uppercase px-1.5 py-0.5 border"
                      style={{
                        color: s.score >= 15 ? "#EAB308" : s.score >= 8 ? "#22D3EE" : "#71717A",
                        borderColor:
                          s.score >= 15
                            ? "rgba(234, 179, 8, 0.55)"
                            : s.score >= 8
                              ? "rgba(34, 211, 238, 0.55)"
                              : "rgba(255, 255, 255, 0.15)",
                      }}
                    >
                      {s.score >= 15 ? "HIGH" : s.score >= 8 ? "MED" : "LOW"} · {s.score}
                    </span>
                    <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500">
                      Gap {s.tier_gap} · Aim → {targetTier.short}
                    </span>
                    {done && (
                      <span
                        className="ml-auto font-mono text-[9px] tracking-[0.22em] uppercase border border-emerald-400/50 text-emerald-300 px-1.5 py-0.5"
                        data-testid={`suggestion-done-badge-${s.id}`}
                      >
                        ✓ Done
                      </span>
                    )}
                  </div>

                  {/* Warm → Cold flow */}
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: wTier.color, boxShadow: `0 0 8px ${wTier.glow}` }}
                    />
                    <div className="min-w-0 flex-1">
                      <div
                        className={`text-[12px] text-zinc-100 truncate ${done ? "line-through decoration-emerald-400/50" : ""}`}
                      >
                        {s.warm.name ||
                          `${s.warm.first_name || ""} ${s.warm.last_name || ""}`.trim()}
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {s.warm.company_name}
                        <span className="text-zinc-600"> · </span>
                        <span style={{ color: wTier.color }}>{wTier.short}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-fuchsia-300 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div
                        className={`text-[12px] text-zinc-100 truncate ${done ? "line-through decoration-emerald-400/50" : ""}`}
                      >
                        {s.cold.name ||
                          `${s.cold.first_name || ""} ${s.cold.last_name || ""}`.trim()}
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {s.cold.company_name}
                        <span className="text-zinc-600"> · </span>
                        <span style={{ color: cTier.color }}>{cTier.short}</span>
                      </div>
                    </div>
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: cTier.color, boxShadow: `0 0 8px ${cTier.glow}` }}
                    />
                  </div>

                  <p className="mt-2 text-[11px] text-zinc-400 leading-relaxed">
                    {s.reason}
                  </p>
                  {done && s.completed_by && (
                    <p className="mt-1 font-mono text-[9px] tracking-[0.18em] uppercase text-emerald-300/80">
                      Marked done by {s.completed_by}
                    </p>
                  )}

                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => onOpenSuggestion && onOpenSuggestion(s)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-cyan-400/40 hover:border-cyan-300 hover:bg-cyan-400/10 text-cyan-200 font-mono text-[10px] uppercase tracking-[0.16em]"
                      data-testid={`suggestion-open-${s.id}`}
                    >
                      <MapPin className="w-3 h-3" />
                      View on Map
                    </button>
                    <button
                      type="button"
                      disabled={busySignalId === s.id}
                      onClick={async () => {
                        setBusySignalId(s.id);
                        try {
                          if (done) {
                            await onUncompleteSignal?.(s.id);
                          } else {
                            await onCompleteSignal?.(s.id);
                          }
                        } finally {
                          setBusySignalId(null);
                        }
                      }}
                      data-testid={done ? `suggestion-undo-${s.id}` : `suggestion-complete-${s.id}`}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border font-mono text-[10px] uppercase tracking-[0.16em] disabled:opacity-50 ${
                        done
                          ? "border-white/15 hover:border-white/40 text-zinc-300"
                          : "border-emerald-400/40 hover:border-emerald-300 hover:bg-emerald-400/10 text-emerald-200"
                      }`}
                    >
                      {done ? (
                        <>
                          <Undo2 className="w-3 h-3" />
                          Reopen
                        </>
                      ) : (
                        <>
                          <Check className="w-3 h-3" />
                          Mark Done
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "networks" && (
          <TpaNetworksPanel
            tpaData={tpaData}
            scopedTpaCoverage={scopedTpaCoverage}
            activeTerritory={activeTerritory}
            selectedTpa={selectedTpa}
            onSelectTpa={setSelectedTpa}
            focusedNetworkState={focusedNetworkState}
            onSelectState={setFocusedNetworkState}
            tpaContacts={tpaContacts}
            expandedTpaName={expandedTpaName}
            setExpandedTpaName={setExpandedTpaName}
            showTpaContactsOnMap={showTpaContactsOnMap}
            setShowTpaContactsOnMap={setShowTpaContactsOnMap}
            onAddTpaContact={onAddTpaContact}
            onEditTpaContact={onEditTpaContact}
            onDeleteTpaContact={onDeleteTpaContact}
            onImportTpaContactsCsv={onImportTpaContactsCsv}
          />
        )}

        {tab === "tools" && (
          <div className="p-4 space-y-3" data-testid="tools-panel">
            <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-zinc-500 mb-1">
              // Source Of Record
            </div>
            <div
              className="px-3 py-2.5 border border-white/10 bg-[#0d0d10] text-zinc-400"
              data-testid="hubspot-source-note"
            >
              <div className="flex items-center gap-3">
                <Info className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
                <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-300">
                  Contacts &amp; firms come from HubSpot
                </span>
              </div>
              <p className="mt-2 text-[12px] leading-relaxed text-zinc-500">
                New contacts and firms are created in HubSpot and arrive here on the next
                sync. Connections are made in this app.
              </p>
            </div>

            <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-zinc-500 mb-1 pt-2">
              // Data Safety
            </div>
            <button
              type="button"
              onClick={downloadBackup}
              disabled={backupBusy}
              data-testid="download-backup"
              className="flex items-center justify-between w-full px-3 py-2.5 border border-emerald-400/30 hover:border-emerald-300/70 hover:bg-emerald-400/5 bg-[#0d0d10] text-zinc-200 text-left transition-colors disabled:opacity-50"
              title="Download a JSON snapshot of all contacts, firms, and connections"
            >
              <span className="flex items-center gap-3">
                <Download className="w-3.5 h-3.5 text-emerald-300" />
                <span className="font-mono text-[12px] uppercase tracking-[0.15em]">
                  {backupBusy ? "Preparing…" : "Download Backup"}
                </span>
              </span>
              <span className="font-mono text-[9px] tracking-[0.16em] uppercase text-zinc-500">
                JSON
              </span>
            </button>

            <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-zinc-500 mb-1 pt-2">
              // Overlays
            </div>
            <button
              type="button"
              onClick={() => setShowStates(!showStates)}
              data-testid="toggle-states"
              className="flex items-center justify-between w-full px-3 py-2 border border-white/10 hover:border-white/30 bg-[#0d0d10] text-left transition-colors"
            >
              <span className="flex items-center gap-3">
                <Layers className="w-3.5 h-3.5 text-zinc-400" />
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-200">
                  State Boundaries
                </span>
              </span>
              <span className="font-mono text-[10px] text-zinc-500">{showStates ? "on" : "off"}</span>
            </button>
            <button
              type="button"
              onClick={() => setChoropleth(!choropleth)}
              data-testid="toggle-choropleth"
              className={`flex items-center justify-between w-full px-3 py-2 border text-left transition-colors ${
                choropleth
                  ? "border-cyan-400/60 bg-cyan-400/5"
                  : "border-white/10 hover:border-white/30 bg-[#0d0d10]"
              }`}
            >
              <span className="flex items-center gap-3">
                <Sparkles className={`w-3.5 h-3.5 ${choropleth ? "text-cyan-300" : "text-zinc-400"}`} />
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-200">
                  Density Choropleth
                </span>
              </span>
              <span className="font-mono text-[10px] text-zinc-500">{choropleth ? "on" : "off"}</span>
            </button>
          </div>
        )}
      </div>

      {/* Footer / cursor state */}
      <div className="px-4 py-2 border-t border-white/10 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-600 flex items-center justify-between">
        <span className="rotate-hint items-center gap-1 text-cyan-400/80">
          <RotateCcw className="w-3 h-3" />
          Rotate for map
        </span>
        <span className="hidden sm:inline">Signal · Real Data</span>
        <span className="text-zinc-500 flex items-center gap-1" data-testid="footer-state-readout">
          <ChevronRight className="w-3 h-3" />
          {hoveredState ? STATE_NAME[hoveredState] || hoveredState : "—"}
        </span>
      </div>
    </aside>
  );
}
